import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { AgGridReact } from 'ag-grid-react';
import CustomLoadingCellRenderer from '../CampaignTable/customLoadingCellRenderer';
import { PlacementDetailsTableService } from '../../../placements';
import { VendorMappingService } from '../../../admin';
import { AlertMessagesService, OmniIconComponent } from '../../../../shared';
import '../../../../styles/copyPlacementsDialog.scss';
import { PlacementUtils } from '../../../placements';
import { MediaOpsService } from '../../../../shared';
import { CampaignPrefetch } from '../../index';

const SKELETON_ROWS = [{ isLoadingRow: true, id: 'skeleton-loading' }];

/**
 * CopyPlacementsDialog
 *
 * Lets a user select placements from a source campaign (left panel) and
 * one or more destination campaigns (right panel), then copies the selected
 * placements into those campaigns.
 *
 * Props:
 *  isOpen            {boolean}  – controls dialog visibility
 *  sourceCampaign    {object}   – the campaign whose placements are shown on the left
 *  onClose           {Function} – called when the dialog should close
 *  versionOneApi     {string}   – 'V1' | 'V2' API version flag
 * systemParametersData {object}   – System parameters data containing buying system info for vendor mapping
 */
const CopyPlacementsDialog = ({
    isOpen,
    sourceCampaign,
    onClose,
    versionOneApi,
    systemParametersData,
    omniProfile,
    allTaxonomyData,
}) => {
    const [placements, setPlacements] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [isLoadingPlacement, setLoadingPlacement] = useState(false);
    const [copying, setCopying] = useState(false);
    const [selectedPlacements, setSelectedPlacements] = useState([]);
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [isLoadingChild, setIsLoadingChild] = useState(false);
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

    const placementsGridRef = useRef(null);
    const campaignsGridRef = useRef(null);

    const vendorMappingMap = {};

    // Re-load placements whenever the dialog is opened for a different source campaign
    useEffect(() => {
        if (isOpen && sourceCampaign?.id) {
            setLoadingPlacement(true);
            loadVendorMapping();
            loadCampaigns();
        }
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, sourceCampaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetState = () => {
        setPlacements([]);
        setCampaigns([]);
        setSelectedPlacements([]);
        setSelectedCampaigns([]);
    };

    const loadVendorMapping = () => {
            let publisherList = (sourceCampaign?.partners || []).join(',');
            let sourceSystem = systemParametersData?.data?.latestClientCode?.[0]?.buyingSystem || '';
            VendorMappingService.getPlannedMappings(publisherList, sourceSystem)
                .then((vendorMappings) => {
                    vendorMappingMap['sourceSystem'] = sourceSystem;
                    if (vendorMappings?.data?.length) {
                        for (let i = 0; i < vendorMappings.data.length; i++) {
                            vendorMappingMap[vendorMappings.data[i]?.planned_value] = vendorMappings.data[i]?.mapped_publishers.map(publisher => ({
                                code: publisher.supplier_code?.toString(),
                                name: publisher.supplier_code + ' ' + publisher.platform_value
                            }));
                        }
                    }
                    loadPlacements();
                }, () => {
                    loadPlacements();
                });
        }

    const loadPlacements = () => {
        const fetchPage = (hasNext, accumulated) =>
            PlacementDetailsTableService.getPlacementsWithDelivered(
                sourceCampaign.id,
                { is_delete: false, page_no: 1, page_size: 2500, 'pmds-version': 'v3' },
                versionOneApi,
                hasNext,
            ).then(response => {
                const items = response?.data?.Items || [];
                const collected = [...accumulated, ...items];
                const next = response?.data?.paging?.next || null;
                return next ? fetchPage(next, collected) : collected;
            });

        fetchPage(null, [])
            .then(allPlacements => {
                let placementsWithNestedStructure = PlacementUtils.CreateNestedData(allPlacements, vendorMappingMap, systemParametersData?.data?.isLocal, {}, sourceCampaign);
                setPlacements(placementsWithNestedStructure);
            })
            .catch(() => {
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger',
                    title: 'Unable to load placements',
                    bodyMsgArr: [],
                }]);
            })
            .finally(() => {
                setLoadingPlacement(false);
            });
    };

    const transformCampaignsWithNested = (items) =>
        items.map(campaign =>
            campaign.child_count && campaign.child_count > 0
                ? { ...campaign, nestedData: [{ campaign_taxonomy: 'Loading Child...', id: campaign.id + 'child' }] }
                : campaign
        );

    const loadCampaigns = async (parentId) => {
        let queryParam = {};
        if(parentId) {
            queryParam = { 'source': 'children', 'parent-id': parentId };
        } else {
            queryParam = { 'source': 'planned', 'page-count': 2500 };
            setIsLoadingCampaigns(true);

            // Try the prefetched cache before hitting the network.
            try {
                const cached = await CampaignPrefetch.getCampaigns();
                if (cached) {
                    setCampaigns(transformCampaignsWithNested(cached));
                    setIsLoadingCampaigns(false);
                    return;
                }
            } catch {
                // Fall through to the API call below.
            }
        }

        MediaOpsService.getCampaignData( queryParam, null, { 'is-archive': false }).then(response => {
                const responseData = response?.data?.Items || [];

                // Update the parent campaign with its children
                if(parentId) {
                    setCampaigns(prevCampaigns => {
                        const updatedCampaigns = prevCampaigns.map(campaign => {
                            if (campaign.id === parentId) {
                                return {
                                    ...campaign,
                                    nestedData: responseData,
                                    nested: true, // Mark as loaded to prevent re-fetching on expand
                                };
                            }
                            return campaign;
                        });
                        return updatedCampaigns;
                    });
                    setIsLoadingChild(false);
                    return;
                }

                // Store in cache for future dialog opens.
                CampaignPrefetch.setCampaigns(responseData).catch(() => {});

                setCampaigns(transformCampaignsWithNested(responseData));
                setIsLoadingCampaigns(false);
            })
            .catch(() => {
                setIsLoadingChild(false);
                setIsLoadingCampaigns(false);
                AlertMessagesService.showMultipleAlerts([{
                    type: 'danger',
                    title: 'Unable to load campaigns',
                    bodyMsgArr: [],
                }]);
            });
    };

    const onExpandClick = (event) => {
        if (event?.expanded) {
            const node = event.node;
            if (!node.data.nested) {
                setIsLoadingChild(true);
                loadCampaigns(node.data.id);
            }
        }
    };

    // ── Left panel – Placements grid ────────────────────────────────────────
    const placementColumnDefs = useMemo(() => [
        // Group placements by their mapped channel (Audio, Video, Display, …)
        {
            headerName: "PLACEMENTS",
            field: "placement_taxonomy",
            wrapText: true,
            autoHeight: true,
            width: 900,
            minWidth: 500,
            flex: 1,
            showRowGroup: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            cellRendererSelector: params => {
                if (params.data?.isLoadingRow) return { component: CustomLoadingCellRenderer };
                return { component: 'agGroupCellRenderer' };
            },
            cellRendererParams: {
                suppressCount: true,
            },
            cellStyle: params => {
                return { left: 0 };
            }
        },
    ], []);

    // ── Right panel – Campaigns grid ─────────────────────────────────────────
    const campaignColumnDefs = useMemo(() => [
        {
            headerName: "CAMPAIGNS",
            valueGetter: params => {
                if(params.data?.data_source?.toLowerCase() === 'planit') {
                    return params.data.campaign_display_name;
                }
                return params.data.campaign_taxonomy;
            },
            wrapText: true,
            autoHeight: true,
            width: 900,
            flex: 1,
            showRowGroup: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            cellRendererSelector: params => {
                if (params.data?.isLoadingRow) return { component: CustomLoadingCellRenderer };
                return { component: 'agGroupCellRenderer' };
            },
            cellRendererParams: {
                suppressCount: true,
            },
            cellStyle: params => {
                return { left: 0 };
            }
        },
    ], []);

    const defaultColDef = {
        sortable: false,
        filter: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
    };

    // ── Selection handlers ────────────────────────────────────────────────────
    const onPlacementSelectionChanged = () => {
        const selectedSet = new Set();
        placementsGridRef.current?.api?.forEachNode((node) => {
            if (node.isSelected()) {
                let current = node;
                while (current) {
                    if (current.data && !['Channel', 'Publisher'].includes(current.data?.title)) {
                        const allChildrenSelected = current.childrenAfterGroup?.every(child => child.isSelected());
                        if (!allChildrenSelected) {
                            break;
                        }
                        selectedSet.add(current.data);
                    }
                    current = current.parent;
                }
            }
        });
        setSelectedPlacements(Array.from(selectedSet));
    };

    const onCampaignSelectionChanged = () => {
        const selected = (campaignsGridRef.current?.api?.getSelectedNodes() || [])
            .filter(n => n.data)
            .map(n => n.data);
        setSelectedCampaigns(selected);
    };

    // ── Copy action ───────────────────────────────────────────────────────────
    const handleCopy = async () => {
        if (!selectedPlacements.length || !selectedCampaigns.length) return;

        setCopying(true);
        try {
            const sourceCampaignId = encodeURIComponent(sourceCampaign.synthetic_id);
            const taxonomies = PlacementDetailsTableService.buildTaxonomyStrings(allTaxonomyData);
            const targetCampaign = selectedCampaigns[0];
            const payload = {
                target_campaign_synthetic_id: targetCampaign.synthetic_id,
                'placement-ids': selectedPlacements.map(p => ({
                    placement_id: p.id,
                    taxonomy_client_id: p.taxonomy_client_id || null,
                    taxonomy_schema_id: p.taxonomy_schema_id || null,
                })),
                createdBy: omniProfile?.email,
                taxonomies,
                is_currency_local: systemParametersData?.data?.isLocal ?? false,
                campaign_netmedia: sourceCampaign?.campaign_total_budget,
                campaign_netmedia_local: sourceCampaign?.campaign_total_budget_local,
            };

            await MediaOpsService.copyPlacements(sourceCampaignId, payload);

            AlertMessagesService.showSuccessToastMessage('Placements copied successfully');
            onClose();
        } catch {
            AlertMessagesService.showErrorToastMessage('Unable to copy placements');
        } finally {
            setCopying(false);
        }
    };

    if (!isOpen) return null;

    const canCopy = selectedPlacements.length > 0 && selectedCampaigns.length > 0;

    return ReactDOM.createPortal(
        <div className="copy-placements-overlay">
            <div className="copy-placements-dialog">

                {/* ── Header ──────────────────────────────────────────────── */}
                <div className="copy-placements-header">
                    <h2 className="copy-placements-title">Copy Campaigns Placements</h2>
                    <button
                        className="tertiary"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <OmniIconComponent icon-id="omni:interactive:close" className='is-size-1 close-icon' />
                    </button>
                </div>

                {/* ── Body: two side-by-side panels ───────────────────────── */}
                <div className="copy-placements-body">

                    {/* Left – Placements */}
                    <div className="copy-panel">
                        <p className="copy-panel__subtitle">
                            Select the Placements from Source
                        </p>
                        <div className="copy-panel__grid">
                            <AgGridReact
                                ref={placementsGridRef}
                                rowData={isLoadingPlacement ? SKELETON_ROWS : placements}
                                columnDefs={placementColumnDefs}
                                defaultColDef={defaultColDef}
                                treeData={true}
                                treeDataChildrenField={"nestedData"}
                                getRowId={(params) => params?.data?.id?.toString()}
                                rowSelection={{
                                    mode: "multiRow",
                                    groupSelects: "descendants",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: true,
                                    enableClickSelection: false,
                                    isRowSelectable: (node) => !node.data?.isLoadingRow,
                                }}
                                groupDefaultExpanded={-1}
                                groupDisplayType={"custom"}
                                suppressServerSideFullWidthLoadingRow={true}
                                onSelectionChanged={onPlacementSelectionChanged}
                                domLayout="normal"
                                ensureDomOrder ={true}
                                rowBuffer={10}
                            />
                        </div>
                    </div>

                    {/* Right – Campaigns */}
                    <div className="copy-panel">
                        <p className="copy-panel__subtitle">
                            Choose the Destination to Copy the Placements
                        </p>
                        <div className="copy-panel__grid">
                            {isLoadingChild && (
                                <div className="copy-panel__loading-overlay">
                                </div>
                            )}
                            <AgGridReact
                                ref={campaignsGridRef}
                                rowData={isLoadingCampaigns ? SKELETON_ROWS : campaigns}
                                columnDefs={campaignColumnDefs}
                                defaultColDef={defaultColDef}
                                treeData={true}
                                treeDataChildrenField={"nestedData"}
                                getRowId={(params) => params?.data?.id?.toString()}
                                rowSelection={{
                                    mode: "singleRow",
                                    checkboxLocation: 'autoGroupColumn',
                                    hideDisabledCheckboxes: true,
                                    enableClickSelection: false,
                                    isRowSelectable: (node) =>  {
                                       if(node.data?.isLoadingRow || node.data?.id === sourceCampaign?.id || node?.data?.id?.toString().includes("child") || node?.data?.data_source?.toLowerCase() === 'planit') {
                                           return false;
                                       }
                                       return true;
                                    }
                                }}
                                onRowGroupOpened={onExpandClick}
                                groupDisplayType={"custom"}
                                suppressServerSideFullWidthLoadingRow={true}
                                onSelectionChanged={onCampaignSelectionChanged}
                                domLayout="normal"
                                ensureDomOrder={true}
                                rowBuffer={10}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Footer ──────────────────────────────────────────────── */}
                <div className="copy-placements-footer">
                    <button
                        className="tertiary"
                        onClick={onClose}
                        disabled={copying}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCopy}
                        disabled={!canCopy || copying}
                        className={!canCopy ? 'secondary' : ''}
                    >
                        {copying ? 'Copying…' : 'Copy Placements'}
                    </button>
                </div>

            </div>
        </div>,
        document.body,
    );
};

export default CopyPlacementsDialog;
